/**
 * DynamicFieldControl - Renders form controls based on field metadata
 *
 * Dynamically renders different control types based on field definition:
 * - SELECT fields (3+ options): Button group (wraps to multiple rows)
 * - BOOLEAN fields (2 options): Button group (wraps to multiple rows)
 * - TEXT fields (0-1 options): Single-line text input
 *
 * Requirement: ES-REQ-013, ES-REQ-014, ES-REQ-015
 */

import React from 'react';
import styled from 'styled-components';
import type { FieldDefinition } from '@/types/labeling';

interface DynamicFieldControlProps {
  fieldDef: FieldDefinition;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
}

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: #555;
  margin-bottom: 8px;
`;

const RequiredIndicator = styled.span`
  color: #d32f2f;
  margin-left: 4px;
  font-weight: var(--font-weight-normal);
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const OptionButton = styled.button<{ $selected: boolean }>`
  padding: 4px 12px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  border: 1px solid ${props => props.$selected ? 'var(--accent-feedback)' : '#ddd'};
  border-radius: 4px;
  background-color: ${props => props.$selected ? 'var(--accent-feedback)' : 'var(--bg-card)'};
  color: ${props => props.$selected ? 'var(--text-inverse)' : 'var(--text-dark)'};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: ${props => props.$selected ? 'var(--color-blue-500)' : 'var(--bg-panel)'};
    border-color: ${props => props.$selected ? 'var(--color-blue-500)' : 'var(--accent-feedback)'};
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: var(--font-size-base);
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-feedback);
  }

  &:disabled {
    background-color: var(--bg-panel);
    cursor: not-allowed;
  }
`;

export const DynamicFieldControl: React.FC<DynamicFieldControlProps> = ({
  fieldDef,
  value,
  onChange,
  disabled = false
}) => {
  const { name, type, options } = fieldDef;
  const fieldId = `field-${name.toLowerCase().replace(/\s+/g, '-')}`;

  // SELECT type: 3+ options - render button group
  if (type === 'select' && options && options.length >= 3) {
    return (
      <FormGroup>
        <Label>
          {name}
          {fieldDef.required && <RequiredIndicator>*</RequiredIndicator>}
        </Label>
        <ButtonGroup>
          {options.map((option) => (
            <OptionButton
              key={option}
              type="button"
              $selected={value === option}
              onClick={() => onChange(option)}
              disabled={disabled}
            >
              {option}
            </OptionButton>
          ))}
        </ButtonGroup>
      </FormGroup>
    );
  }

  // BOOLEAN type: 2 options - render button group
  if (type === 'boolean' && options && options.length === 2) {
    return (
      <FormGroup>
        <Label>
          {name}
          {fieldDef.required && <RequiredIndicator>*</RequiredIndicator>}
        </Label>
        <ButtonGroup>
          {options.map((option) => (
            <OptionButton
              key={option}
              type="button"
              $selected={value === option}
              onClick={() => onChange(option)}
              disabled={disabled}
            >
              {option}
            </OptionButton>
          ))}
        </ButtonGroup>
      </FormGroup>
    );
  }

  // TEXT type: 0-1 options - render text input
  return (
    <FormGroup>
      <Label htmlFor={fieldId}>
        {name}
        {fieldDef.required && <RequiredIndicator>*</RequiredIndicator>}
      </Label>
      <TextInput
        id={fieldId}
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${name}...`}
        disabled={disabled}
      />
    </FormGroup>
  );
};

export default DynamicFieldControl;
